import { useEffect, useRef, useState, type RefObject } from "react";

/** The first field a person fills: what the focus moves to. */
const FIRST_FIELD = "input:not([disabled]):not([type=hidden]), textarea:not([disabled]), select:not([disabled])";

export interface FocusOnInputs {
  /** The region that holds the picked Workflow's inputs; it takes the focus when it has no field. */
  readonly inputsRef: RefObject<HTMLDivElement | null>;
  /** A Workflow was picked: its first input takes the focus once `loaded`. */
  readonly picked: () => void;
}

/**
 * After a person picks a Workflow, the focus goes to its first input once its plan is shown
 * (F98); each pick asks again, also of the Workflow already chosen (TR-B5-06). The start and a
 * Thread's switch to another Workflow (F126) pick the same way.
 */
export function useFocusOnInputs(loaded: boolean): FocusOnInputs {
  const inputsRef = useRef<HTMLDivElement>(null);
  const [request, setRequest] = useState(0);
  const handled = useRef(0);
  useEffect(() => {
    const region = inputsRef.current;
    if (request === handled.current || !loaded || region === null) return;
    handled.current = request;
    (region.querySelector<HTMLElement>(FIRST_FIELD) ?? region).focus();
  }, [loaded, request]);
  return { inputsRef, picked: () => { setRequest((current) => current + 1); } };
}
